<script setup lang="ts">
import { onMounted, ref, computed, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTheme } from '@/composables/useTheme'
import { useNotifications } from '@/composables/useNotifications'
import Icon from './components/atoms/Icon.vue'
import Button from './components/atoms/Button.vue'
import Badge from './components/atoms/Badge.vue'
import ToastContainer from './components/organisms/ToastContainer.vue'
import NotificationToggle from './components/molecules/NotificationToggle.vue'
import StatusBar from './components/layout/StatusBar.vue'
import ConfigurationExplorer from '@/components/organisms/ConfigurationExplorer.vue'
import ConfigurationWorkspace from '@/components/organisms/ConfigurationWorkspace.vue'
import { useConfigurationStore } from '@/stores/configuration'
import TemplateDiagnosticsFlyout from '@/components/organisms/TemplateDiagnosticsFlyout.vue'
import { useAuthStore } from '@/stores/authStore'

const route = useRoute()
const router = useRouter()
const { theme, toggleTheme, initTheme } = useTheme()
const configStore = useConfigurationStore()
const authStore = useAuthStore()

// Notifications
const {
  notifications,  // Full list (for history)
  activeNotifications,  // Filtered active (for toasts)
  handleExpand,
  handleCollapse,
  handleClose,
  handleAction,
  handleRemoved,
  markAsDisplayedInToast,
  panelOpen,
} = useNotifications()

onMounted(async () => {
  initTheme()
  // Load roles cache early (anonymous endpoint, needed for role name lookups)
  configStore.loadRoles()
  // Initialize authentication
  await authStore.initialize()
  
  // Check if user is authenticated, if not redirect to login
  if (!authStore.isAuthenticated && route.name !== 'login') {
    console.log('[App] User not authenticated, redirecting to login')
    router.push({ name: 'login', query: { redirect: route.fullPath } })
    return
  }
  
  // Load precedence levels from API on app initialization (only if authenticated)
  if (authStore.isAuthenticated) {
    configStore.loadPrecedenceLevels()
  }
})

// Sign out handler
async function handleSignOut() {
  try {
    await authStore.logout()
    router.push({ name: 'login' })
  } catch (error) {
    console.error('Sign out failed:', error)
  }
}

// Determine if current route uses editor layout (sidebar + workspace)
const isEditorLayout = computed(() => route.name === 'editor')

// Sidebar resizing state
const sidebarWidth = ref(300) // px initial
const minSidebar = 200
const maxSidebar = 640
const dividerWidth = 4
const isResizing = ref(false)
let startX = 0
let startWidth = 0

function onDividerMouseDown(e: MouseEvent) {
  isResizing.value = true
  startX = e.clientX
  startWidth = sidebarWidth.value
  document.body.style.cursor = 'col-resize'
  document.body.style.userSelect = 'none'
}

function onMouseMove(e: MouseEvent) {
  if (!isResizing.value) return
  const delta = e.clientX - startX
  let next = startWidth + delta
  next = Math.max(minSidebar, Math.min(maxSidebar, next))
  sidebarWidth.value = next
}

function stopResizing() {
  if (!isResizing.value) return
  isResizing.value = false
  document.body.style.cursor = ''
  document.body.style.userSelect = ''
}

onMounted(() => {
  window.addEventListener('mousemove', onMouseMove)
  window.addEventListener('mouseup', stopResizing)
})
onBeforeUnmount(() => {
  window.removeEventListener('mousemove', onMouseMove)
  window.removeEventListener('mouseup', stopResizing)
})

// Check if we're on the login page
const isLoginPage = computed(() => route.name === 'login')

const appGridStyle = computed(() => {
  // Login page: full screen content only
  if (isLoginPage.value) {
    return {
      gridTemplateRows: '1fr',
      gridTemplateColumns: '1fr',
      gridTemplateAreas: '"content"'
    }
  }
  
  // Normal pages: header, content, status bar
  return {
    gridTemplateColumns: isEditorLayout.value 
      ? `${sidebarWidth.value}px ${dividerWidth}px 1fr` 
      : `0px 0px 1fr`,
    '--sidebar-width': sidebarWidth.value + 'px'
  }
})

// Configuration status for status bar
const modeLabel = computed(() => configStore.isEditMode ? 'Edit Mode' : 'Read-only')
const savedLabel = computed(() => configStore.isDirty ? 'Unsaved' : 'Saved')
const presetCount = computed(() => configStore.presetConfigurations.length)
const resolvedAt = computed(() => configStore.currentConfiguration?.resolvedAt ? new Date(configStore.currentConfiguration.resolvedAt).toLocaleString() : '—')
const tenantName = computed(() => {
  // Show display name if we have tenant data, fallback to API name
  const selectedTenantName = configStore.editorState.selectedTenant
  return selectedTenantName || 'No tenant'
})

// Template diagnostics (status bar badge + flyout)
const showDiagnostics = ref(false)
const diagnosticsCount = computed(() => configStore.templateDiagnosticsErrorCount)
const diagnosticsErrors = computed(() => configStore.templateDiagnosticsErrors)
function toggleDiagnostics() { showDiagnostics.value = !showDiagnostics.value }
function closeDiagnostics() { showDiagnostics.value = false }
function refreshDiagnostics() { /* Reuse force refresh for now */ configStore.forceRefreshConfiguration?.() }

// Badge shows total notification count (all notifications in history)
const unreadNotificationCount = computed(() => notifications.value.length)

</script>

<template>
  <div class="app-grid" :style="appGridStyle">
    <!-- HEADER (grid-area: header) - Hide on login page -->
    <header v-if="route.name !== 'login'" class="app-header" data-area="header">
      <div class="header-grid">
        <div class="header-title">
          <h1>Configuration Management</h1>
        </div>
        <nav class="header-nav">
          <!--<button
            v-for="item in navItems"
            :key="item.name"
            class="nav-item"
            :class="{ 'nav-item-active': route.name === item.name }"
            @click="$router.push({ name: item.name })"
          >
            <Icon :name="item.icon" :size="16" />
            <span>{{ item.label }}</span>
          </button>-->
          
          <!-- Sign Out Button -->
          <div v-if="authStore.isAuthenticated" class="nav-divider"></div>
          <button
            v-if="authStore.isAuthenticated"
            class="nav-item nav-item-signout"
            @click="handleSignOut"
            title="Sign out"
          >
            <Icon name="sign-out" :size="16" />
            <span>{{ authStore.userInfo?.name || 'Sign Out' }}</span>
          </button>
        </nav>
      </div>
    </header>

    <!-- SIDEBAR (grid-area: sidebar) only for editor layout -->
    <aside v-if="isEditorLayout" class="sidebar" data-area="sidebar">
      <ConfigurationExplorer />
    </aside>

    <!-- DIVIDER (grid-area: split) -->
    <div v-if="isEditorLayout" 
      class="divider" 
      data-area="split" 
      :class="{ 'divider--active': isResizing }"
      @mousedown="onDividerMouseDown"
      role="separator" 
      aria-orientation="vertical" 
      :aria-valuenow="sidebarWidth"
      :aria-valuemin="minSidebar" 
      :aria-valuemax="maxSidebar"
      tabindex="0"
    />

    <!-- CONTENT (grid-area: content) -->
    <main class="content" data-area="content">
      <div v-if="isEditorLayout" class="workspace-wrapper">
        <ConfigurationWorkspace />
      </div>
      <router-view v-else />
    </main>

    <!-- STATUS BAR (grid-area: statusbar) - Hide on login page -->
    <StatusBar v-if="route.name !== 'login'" data-area="statusbar">
      <template #status>
        <div class="status-left">
          <span class="status-item" title="Tenant"><Icon name="user" :size="12" /> {{ tenantName }}</span>
          <span class="status-item" title="Active presets"><Icon name="squares-four" :size="12" /> {{ presetCount }}</span>
          <span class="status-item" title="Resolved timestamp"><Icon name="clock" :size="12" /> {{ resolvedAt }}</span>
        </div>
        <div class="status-spacer" />
        <div class="status-right">
          <span class="status-item" :class="{ 'status-dirty': configStore.isDirty }" title="Mode"><Icon :name="configStore.isEditMode ? 'pencil-simple' : 'lock'" :size="12" /> {{ modeLabel }}</span>
          <span class="status-item" :class="{ 'status-dirty': configStore.isDirty }" title="Save state"><Icon :name="configStore.isDirty ? 'warning-circle' : 'check'" :size="12" /> {{ savedLabel }}</span>
          <Button class="btn btn--icon" variant="secondary" size="small" title="Toggle theme" @click="toggleTheme">
            <Icon :name="theme === 'dark' ? 'moon' : 'sun'" :size="14" />
          </Button>
          <Button
            variant="icon"
            size="medium"
            :aria-expanded="showDiagnostics"
            aria-haspopup="dialog"
            :title="showDiagnostics ? 'Hide template issues' : 'Show template issues'"
            class="diagnostics-button"
            @click="toggleDiagnostics"
          >
            <div class="diagnostics-button__icon-wrapper">
              <Icon name="warning" />
              <Badge
                v-if="diagnosticsCount"
                :text="diagnosticsCount > 99 ? '99+' : diagnosticsCount.toString()"
                variant="info"
                size="small"
                class="diagnostics-button__badge"
              />
            </div>
          </Button>
          <NotificationToggle :count="unreadNotificationCount" @expand="handleExpand" @collapse="handleCollapse" />
        </div>
      </template>
    </StatusBar>

    <!-- Diagnostics Flyout (anchored to status bar right side) -->
    <div v-if="showDiagnostics" class="diagnostics-overlay" @click="closeDiagnostics" />
    <div v-if="showDiagnostics" class="diagnostics-flyout-wrapper">
      <TemplateDiagnosticsFlyout
        :visible="showDiagnostics"
        :errors="diagnosticsErrors"
        @close="closeDiagnostics"
        @refresh="refreshDiagnostics"
      />
    </div>

    <!-- Toast Notifications (overlay) -->
    <ToastContainer
      v-if="!panelOpen"
      :notifications="activeNotifications"
      :z-index="1100"
      @expand="handleExpand"
      @collapse="handleCollapse"
      @close="handleClose"
      @action="handleAction"
      @removed="handleRemoved"
      @displayed="markAsDisplayedInToast"
    />
  </div>
</template>

<style scoped lang="scss">
/* Top-level 3x3 grid */
.app-grid {
  --divider-color: var(--vscode-border);
  --divider-active: var(--vscode-focusBorder);
  display: grid;
  grid-template-rows: auto 1fr auto;
  grid-template-areas:
    "header header header"
    "sidebar split content"
    "statusbar statusbar statusbar";
  height: 100vh; /* lock to viewport */
  background: var(--vscode-editor-background);
  position: relative;
  overflow: hidden; /* prevent page scroll; delegate to editor content */
}

/* Grid area base selectors */
[data-area=header] { grid-area: header; }
[data-area=sidebar] { grid-area: sidebar; min-width: 0; overflow: hidden; }
[data-area=split] { grid-area: split; }
[data-area=content] { grid-area: content; min-width: 0; min-height: 0; }
[data-area=statusbar] { grid-area: statusbar; }

/* Header subgrid */
.app-header {
  background: var(--vscode-titleBar-activeBackground);
  color: var(--vscode-titleBar-activeForeground);
  border-bottom: 1px solid var(--vscode-widget-border);
  position: sticky; /* keep at top if page taller */
  top: 0;
  z-index: 10;
  display: grid;
  padding: 4px 16px;
}

.header-grid {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 24px;
  align-items: center;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 12px;
  h1 { margin: 0; font-size: 18px; font-weight: 600; }
}

.header-nav { display: flex; gap: 4px; justify-content: flex-end; flex-wrap: wrap; align-items: center; }

.nav-divider {
  width: 1px;
  height: 20px;
  background: var(--vscode-titleBar-inactiveForeground);
  opacity: 0.3;
  margin: 0 4px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: transparent;
  border: none;
  color: var(--vscode-titleBar-inactiveForeground);
  cursor: pointer;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  transition: background .15s ease;
  &:hover { background: var(--vscode-toolbar-hoverBackground); color: var(--vscode-titleBar-activeForeground); }
  &.nav-item-active { background: var(--vscode-button-background); color: var(--vscode-button-foreground); }
  &.nav-item-active:hover { background: var(--vscode-button-hoverBackground); }
  &.nav-item-signout {
    color: var(--vscode-titleBar-activeForeground);
    &:hover {
      background: var(--vscode-statusBarItem-errorBackground, #f14c4c);
      color: var(--vscode-statusBarItem-errorForeground, #fff);
    }
  }
}

/* Sidebar overrides to ensure internal scroll works */
.sidebar { display: flex; flex-direction: column; min-height: 0; border-right: 1px solid var(--vscode-sideBar-border, var(--vscode-widget-border)); }

/* Divider */
.divider {
  width: 100%;
  cursor: col-resize;
  background: var(--divider-color);
  position: relative;
  transition: background-color .1s linear;
}
.divider:hover, .divider--active { background: var(--divider-active); }
.divider::after {
  content: '';
  position: absolute;
  top: 0; bottom: 0; left: 50%; width: 1px;
  transform: translateX(-50%);
  background: var(--vscode-sash-hoverBorder, transparent);
  opacity: .5;
}
/* Focus outline for accessibility */
.divider:focus { outline: 2px solid var(--vscode-focusBorder); outline-offset: -2px; }

/* Content area */
.content { display: flex; flex-direction: column; min-height: 0; overflow: hidden; }
/* Make only the workspace scrollable (editor area) */
.workspace-wrapper { flex: 1; min-height: 0; display: flex; flex-direction: column; overflow: auto; overscroll-behavior: contain; }

/* Responsive: collapse sidebar below 760px */
@media (max-width: 760px) {
  .app-grid { grid-template-columns: 0px 0px 1fr !important; }
  .sidebar, .divider { display: none !important; }
}

/* Theme toggle styles */
.theme-toggle__icon-container { display: flex; align-items:center; justify-content:center; }

.status-left, .status-right { display: flex; align-items: center; gap: 8px; overflow: visible; }
.status-left { flex-shrink: 0; }
.status-right { 
  flex-shrink: 0; 
  
  /* Ensure buttons in status bar are properly aligned */
  :deep(.btn) {
    height: 100%;
    min-height: auto;
    padding: 0 4px;
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
.status-spacer { flex: 1 1 auto; }
.status-item { display: inline-flex; align-items: center; gap: 4px; padding: 0 4px; font-size: 11px; white-space: nowrap; position: relative; }
.status-item :deep(button.btn) { padding: 0 4px; }
.status-dirty { color: var(--vscode-editorWarning-foreground); }
.status-item--clickable { cursor: pointer; }
.diagnostics-overlay { position: fixed; inset:0; background:transparent; z-index: 2400; }
.diagnostics-flyout-wrapper { position: absolute; bottom: var(--status-bar-height); right: 4px; z-index: 2401; }
.diagnostics-button { position: relative; }
.diagnostics-button__icon-wrapper { position: relative; display:flex; align-items:center; justify-content:center; }
.diagnostics-button__badge { 
  position: absolute; 
  top: -4px; 
  right: -6px; 
  z-index: 1;
  pointer-events: none; 
  padding: 0 3px 2px 3px; 
  font-size: 10px; 
  min-height: min-content;
  line-height: 1;
  border-radius: 5px;
  max-width: max-content;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
